import React, { useCallback, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { possibleGames } from "../fortune-wheel-data";

interface FortuneWheelTableProps {
    sectors?: HallFortuneWheelSector[];
    setSectors: (sectors: HallFortuneWheelSector[]) => void;
}

function updateSectorType(sector: HallFortuneWheelSector, type: typeof sector.type): HallFortuneWheelSector {
    if (sector.type === type) {
        return sector;
    }
    if (type === 1) {
        return { type: 1, amount: sector.amount, active: sector.active };
    } else if (type === 2) {
        const game = Object.values(possibleGames)[0];
        return {
            type: 2,
            amount: sector.amount,
            game: game.game,
            bet: 1,
            denomination: 100,
            active: sector.active,
        }
    }
    throw new Error("unknown type");
}

const FortuneWheelTable: React.FC<FortuneWheelTableProps> = ({ sectors, setSectors }) => {
    const safeSectors: HallFortuneWheelSector[] = useMemo(() => {
        if (sectors) {
            return sectors.slice(0, 12);
        }
        return new Array(12).fill(0).map(() => ({ type: 1, amount: 0, active: "ENABLE" }));
    }, [sectors]);
    const [t] = useTranslation();
    const changeGame = useCallback((idx: number, gameId: number) => {
        setSectors([...safeSectors.slice(0, idx), { ...safeSectors[idx] as HallFortuneWheelSectorFreespeen, game: gameId }, ...safeSectors.slice(idx + 1)])
    }, [safeSectors, setSectors]);
    const changeDenomination = useCallback((idx: number, denomination: number) => {
        setSectors([...safeSectors.slice(0, idx), { ...safeSectors[idx] as HallFortuneWheelSectorFreespeen, denomination }, ...safeSectors.slice(idx + 1)])
    }, [safeSectors, setSectors]);
    const gamesOptions = useMemo(() => <>
        {possibleGames.map(game => <option key={game.game} value={game.game}>{game.name}</option>)}
    </>, []);
    const denominationsOptions = useMemo(() => <>
        {[100, 50, 10, 1].map(denomination => <option key={denomination} value={denomination}>{denomination / 100}</option>)}
    </>, []);
    return <Table size="small" stickyHeader>
        <TableHead>
            <TableRow>
                <TableCell>{t("ru.halls.wager.fortune_wheel.sectors.idx")}</TableCell>
                <TableCell>{t("ru.halls.wager.fortune_wheel.sectors.win")}</TableCell>
                <TableCell>{t("ru.halls.wager.fortune_wheel.sectors.active")}</TableCell>
            </TableRow >
        </TableHead >
        <TableBody>
            {safeSectors.map((sector, idx) => <TableRow key={idx}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell style={{ display: "flex", gap: "2px" }}>
                    <select value={sector.type} onChange={e => {
                        const newSector: HallFortuneWheelSector = updateSectorType(safeSectors[idx], +e.target.value as typeof sector.type);
                        setSectors([...safeSectors.slice(0, idx), newSector, ...safeSectors.slice(idx + 1)])
                    }}>
                        <option value="1">Баланс</option>
                        <option value="2">Фриспины</option>
                    </select>
                    <input type="number" value={sector.amount} onChange={e => setSectors([...safeSectors.slice(0, idx), { ...safeSectors[idx], amount: +e.target.value }, ...safeSectors.slice(idx + 1)])} />
                    {sector.type === 2 && <>
                        <select value={sector.game} onChange={e => changeGame(idx, +e.target.value)}>
                            {gamesOptions}
                        </select>
                        <label> Деноминация
                            <select style={{ textAlign: "center" }} value={sector.denomination} onChange={e => changeDenomination(idx, +e.target.value)}>
                                {denominationsOptions}
                            </select>
                        </label>
                    </>}
                </TableCell>
                <TableCell>
                    <select value={sector.active} onChange={e => {
                        const newSector: HallFortuneWheelSector = {...safeSectors[idx], active: e.target.value as HallFortuneWheelSectorActive};
                        setSectors([...safeSectors.slice(0, idx), newSector, ...safeSectors.slice(idx + 1)])
                    }}>
                        <option value="ENABLE">{t("ru.halls.wager.fortune_wheel.active.ENABLE")}</option>
                        <option value="DISABLE">{t("ru.halls.wager.fortune_wheel.active.DISABLE")}</option>
                        <option value="NO_BONUS">{t("ru.halls.wager.fortune_wheel.active.NO_BONUS")}</option>
                    </select>
                </TableCell>
            </TableRow>)}
        </TableBody >
    </Table >;
}

export default FortuneWheelTable;
